import $ from 'jquery'
import { Controller } from 'stimulus'
import { waitForTransitionsToComplete } from '~/common/helpers/animation'

// How to use:
//
// 1. Add the controller to a containing element:
//    data-controller="revealable"
//    <%= stimulus_controller('revealable') %>
//    data: stimulus_controller('revealable', as_hash: true)
//
//  2. Set the things you want to be toggled on/off
//     data-revealable-target="toggle"
//     <%= stimulus(target: 'toggle', identifier: 'revealable') %>
//     data: stimulus(target: 'toggle', identifier: 'revealable', as_hash: true)
//
//  3. Add the action to some element on click
//     data-action="click->revealable#toggle"
//     stimulus(identifier: 'revealable', actions: { click: :toggle }, as_hash: true)
//

export default class extends Controller {
  static targets = ['toggle']
  static values = { scope: String }

  // will toggle all toggleTargets, and focus the first visible input/textarea
  toggle(event) {
    if (this.#shouldPreventToggle(event)) return

    // If cmd/ctrl-click, toggle all revealables on the page
    if (event?.metaKey || event?.ctrlKey) {
      // Find the main content element (not an icon) to determine the target state
      // Icons are created by revealable_toggle and have the 'fa' class (Font Awesome)
      const contentElement = this.toggleTargets.find(
        (elm) => !elm.classList.contains('fa')
      )
      const elementToCheck = contentElement || this.toggleTargets[0]
      // Determine the target state: what state will this element be in after toggling?
      const targetIsHidden = !this.isHidden(elementToCheck)
      this.toggleAllRevealables(targetIsHidden)
      return
    }

    this.toggleTargets.forEach((elm) => {
      this.toggleElement(elm)
    })
  }

  // Set all revealable controllers on the page with the same scope to a target state
  toggleAllRevealables(targetIsHidden) {
    this.application.controllers
      .filter(
        (c) =>
          c.identifier === 'revealable' &&
          (!this.hasScopeValue || c.scopeValue === this.scopeValue)
      )
      .forEach((c) => {
        // Find the content element to check if this revealable needs to change
        const contentElement = c.toggleTargets.find(
          (elm) => !elm.classList.contains('fa')
        )
        if (contentElement) {
          const isCurrentlyHidden = c.isHidden(contentElement)
          // Only toggle all targets (icons + content) if content needs to change state
          if (isCurrentlyHidden !== targetIsHidden) {
            c.toggleTargets.forEach((elm) => c.toggleElement(elm))
          }
        }
      })
  }

  hide() {
    this.toggleTargets.forEach((elm) => {
      if (!this.isHidden(elm)) this.toggleElement(elm)
    })
  }

  show() {
    this.toggleTargets.forEach((elm) => {
      if (this.isHidden(elm)) this.toggleElement(elm)
    })
  }

  async toggleWithHeightAnimation(e) {
    if (this.#shouldPreventToggle(e)) return
    if (this._isAnimatingAlready) return
    this._isAnimatingAlready = true
    await Promise.all(
      this.toggleTargets.map(async (elm) => {
        if (!this.isAnimatable(elm)) {
          return this.toggleElement(elm)
        }

        const isHidden = this.isHidden(elm)

        const previousTransition = elm.style.transition
        const previousHeight = elm.style.height
        const previousOverflow = elm.style.overflow
        const previousWillChange = elm.style.willChange

        const fromHeight = `${isHidden ? 0 : elm.offsetHeight}px`
        if (isHidden) elm.classList.remove('hidden')
        const toHeight = `${isHidden ? elm.offsetHeight : 0}px`

        elm.style.transition = '0.2s height'
        elm.style.overflow = 'hidden'
        elm.style.height = fromHeight
        elm.style.willChange = 'height'

        void elm.offsetHeight
        elm.style.height = toHeight
        this.fireToggleEvent(elm, isHidden)

        await waitForTransitionsToComplete(elm)
        if (isHidden) $(elm).changed?.()

        if (!isHidden) elm.classList.add('hidden')
        elm.style.transition = previousTransition
        elm.style.height = previousHeight
        elm.style.overflow = previousOverflow
        elm.style.willChange = previousWillChange
        return await waitForTransitionsToComplete(elm)
      })
    )
    this._isAnimatingAlready = false
  }

  isAnimatable(elm) {
    return !elm.classList.contains('js-no-animated-toggle')
  }

  isHidden(elm) {
    return elm.classList.contains('hidden')
  }

  toggleElement(elm) {
    const isHidden = this.isHidden(elm)
    elm.classList.toggle('hidden')
    if (window.getComputedStyle(elm).display !== 'none') {
      $(elm).changed().find('input:visible,textarea:visible').first().focus()
    }
    this.fireToggleEvent(elm, isHidden)
  }

  fireToggleEvent(element, visible) {
    this.dispatch('toggled', { target: element, detail: { visible } })
  }

  toggleUntilKeyUp(event) {
    document.addEventListener('keyup', (event) => this.toggle(event), {
      once: true,
    })
    this.toggle(event)
  }

  #shouldPreventToggle(event) {
    return event.target?.closest('.js-prevent-revealable-toggle')
  }
}
